import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ICitizenshipInfo} from '../../../../../models/citizenship-info';
import {Country} from '../../../../../models/country';
import {CountryService} from '../../../../../data-repositories/country-service';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ValidationInfoService} from '../../../../../repositories/validation-info.service';
import {FocusInvalidService} from '../../../../../services/focus-invalid.service';
import {ActivatedRoute} from '@angular/router';
import {Subject, Subscription} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {forbiddenValues} from '../../../../../services/validators';
import {formLog} from '../../../../../services/debug-logger';

@Component({
  standalone: false,
  selector: 'app-citizenship',
  templateUrl: './citizenship.component.html',
  styleUrls: ['./citizenship.component.scss']
})
export class CitizenshipComponent implements OnInit, OnDestroy {

  private _citizenshipModel: ICitizenshipInfo;
  private subscription$: Subscription;
  private destroy$ = new Subject<void>();

  countryList: Array<Country>;
  citizenshipForm: FormGroup;


  @Input()
  set citizenshipModel(value: ICitizenshipInfo) {
    this._citizenshipModel = value;
    this.patchFormFromModel();
  }

  get citizenshipModel(): ICitizenshipInfo {
    return this._citizenshipModel;
  }

  @Input() forbiddenIds: Array<string> = new Array<string>();

  @Output() citizenshipModelChange = new EventEmitter<ICitizenshipInfo>();

  @ViewChild('citizenshipFormElement') citizenshipFormElement: ElementRef;

  constructor(
    countryService: CountryService,
    private formBuilder: FormBuilder,
    private validationService: ValidationInfoService,
    private focusInvalidService: FocusInvalidService,
    private route: ActivatedRoute
  ) {

    countryService.getCountryList().pipe(takeUntil(this.destroy$)).subscribe(cl => {
      this.countryList = cl;
    });
  }

  ngOnInit() {
    this.buildForm();
    this.patchFormFromModel();

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params.validate) {
        this.validationService.markAllFieldsAsTouched(this.citizenshipForm);
        this.focusInvalidService.selectInvalidInput(this.citizenshipFormElement);
      }
    });

    this.subscription$ = this.validationService.onUserUpdate.subscribe(() => {
      this.validationService.markAllFieldsAsTouched(this.citizenshipForm);

      this.focusInvalidService.selectInvalidInput(this.citizenshipFormElement);
      this.validationService.validate(this.citizenshipForm.valid, 'CitizenshipComponent');
    });

    this.subscribeAllInputsOnUpdate();
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();

    this.destroy$.next();
    this.destroy$.complete();
  }

  public updateUser(): void {
    this.citizenshipModelChange.emit(this.citizenshipModel);
  }

  public isRequiredField(fieldName: string): boolean {
    return this.citizenshipForm.controls[fieldName].invalid
      && (this.citizenshipForm.controls[fieldName].dirty || this.citizenshipForm.controls[fieldName].touched)
      && (this.citizenshipForm.controls[fieldName].errors.required || this.citizenshipForm.controls[fieldName].errors.pattern);
  }

  public hasErrorField(fieldName: string, error: string): boolean {
    const control = this.citizenshipForm.controls[fieldName];
    return control.invalid &&
      (control.dirty || control.touched) &&
      control.hasError(error);
  }

  filterCountryList(countryList: Array<Country>, filter: string, includeUSA: boolean = true) {
    if (!countryList) {
      return new Array<Country>();
    }

    if (filter && filter.trim().length > 0) {
      return countryList.filter(x => x.countryName !== 'United States' || includeUSA)
        .filter(x => x.countryTranslation.toLowerCase().startsWith(filter.toLowerCase()));
    }

    return countryList.filter(x => x.countryName !== 'United States' || includeUSA);
  }

  getCountryCode(citizenship: string): string {
    if (citizenship && this.countryList && this.countryList.some(x => x.countryName === citizenship)) {
      return this.countryList.find(x => x.countryName === citizenship).countryCode;
    }

    return '';
  }

  getTranslate(country: string) {
    if (!country || !this.countryList || !this.countryList.some(x => x.countryName === country)) {
      return '';
    }

    return this.countryList.find(x => x.countryName === country).countryTranslation;
  }

  private buildForm() {
    this.citizenshipForm = this.formBuilder.group({
      citizenship: new FormControl('', [Validators.required]),
      countryOfBirth: new FormControl('', [Validators.required]),
      countryIssuence: new FormControl('', [Validators.required]),
      idNumber: new FormControl('', [
        Validators.required,
        Validators.minLength(6),
        forbiddenValues(this.forbiddenIds)
      ]),
    });
  }

  private patchFormFromModel(): void {
    if (!this._citizenshipModel || !this.citizenshipForm) { return; }
    this.citizenshipForm.patchValue({
      citizenship: this._citizenshipModel.citizenship ?? '',
      countryOfBirth: this._citizenshipModel.countryOfBirth ?? '',
      countryIssuence: this._citizenshipModel.countryIssuence ?? '',
      idNumber: this._citizenshipModel.idNumber ?? '',
    }, {emitEvent: false});
    formLog.state('Citizenship', this._citizenshipModel, this.citizenshipForm);
  }

  private syncModelFromForm(): void {
    if (!this._citizenshipModel) { return; }
    const value = this.citizenshipForm.getRawValue();
    this._citizenshipModel.citizenship = value.citizenship;
    this._citizenshipModel.countryOfBirth = value.countryOfBirth;
    this._citizenshipModel.countryIssuence = value.countryIssuence;
    this._citizenshipModel.idNumber = value.idNumber;
    formLog.state('Citizenship', this._citizenshipModel, this.citizenshipForm);
  }

  private subscribeAllInputsOnUpdate() {
    this.citizenshipForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.syncModelFromForm();
      this.updateUser();
    });
  }
}
